/**
 * Arreglo 1 (oct-2026, founder) — Botón flotante dorado «Agenda» (convive con el «+» de crear, lado a lado).
 * Solo barberos (y admin/founder con agenda propia). Nunca clientes. Abre el dock a pantalla completa.
 */
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CalendarDays } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/api';
import { usePwaInstall } from '@/pwa/PwaInstallContext';
import { AgendaDock } from './AgendaDock';
import { tr } from '@/contexts/LanguageContext';

// El banner «Instala Ziyup» (z-70, bottom 72px, ~100px alto) tapa el FAB: lo subimos mientras esté visible.
export const PWA_BANNER_LIFT_PX = 92;
// Ancho fijo de la píldora «Agenda» para que el «+» se coloque a su izquierda sin medir el DOM.
export const AGENDA_FAB_WIDTH_PX = 116;
export const FAB_GAP_PX = 10;

const HIDDEN_PREFIXES = ['/auth', '/admin', '/messages/', '/legal', '/pay/', '/qr/', '/live/', '/judge/onboarding'];
export const AGENDA_FAB_EVENT = 'lineage_agenda_fab';
const ADMIN_ROLES = ['admin', 'super_admin', 'founder_admin'];

const useAgendaEligibility = (user) => {
  const [eligible, setEligible] = useState(false);
  useEffect(() => {
    let alive = true;
    if (!user) { setEligible(false); return undefined; }
    // Manda el rol de VISTA («Ver como»): un founder viendo como cliente no ve Agenda.
    if (user.role === 'barber' || (user.is_barber && !['client', 'spectator', 'brand', 'judge'].includes(user.role))) { setEligible(true); return undefined; }
    if (ADMIN_ROLES.includes(user.role)) {
      api.get('/barbers/me/agenda-eligibility').then((r) => alive && setEligible(Boolean(r?.eligible))).catch(() => alive && setEligible(false));
    } else {
      setEligible(false);
    }
    return () => { alive = false; };
  }, [user?.id, user?.role, user?.is_barber]); // eslint-disable-line react-hooks/exhaustive-deps
  return eligible;
};

export const AgendaFab = () => {
  const { user } = useAuth();
  const location = useLocation();
  const eligible = useAgendaEligibility(user);
  const { bannerVisible } = usePwaInstall();
  const [open, setOpen] = useState(false);
  const [initialTab, setInitialTab] = useState('agenda');

  const hiddenRoute = location.pathname === '/' || HIDDEN_PREFIXES.some((p) => location.pathname.startsWith(p))
    || location.pathname.endsWith('/reservar');
  const visible = Boolean(user) && eligible && !hiddenRoute;

  useEffect(() => {
    window.dispatchEvent(new CustomEvent(AGENDA_FAB_EVENT, { detail: { visible } }));
  }, [visible]);

  useEffect(() => {
    const onOpen = (e) => { setInitialTab(e.detail?.tab || 'agenda'); setOpen(true); };
    window.addEventListener('lineage_agenda_dock_open', onOpen);
    return () => window.removeEventListener('lineage_agenda_dock_open', onOpen);
  }, []);

  if (!visible) return null;
  return (
    <>
      {!open && (
        <motion.button
          type="button"
          onClick={() => { setInitialTab('agenda'); setOpen(true); }}
          whileTap={{ scale: 0.94 }}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          data-testid="agenda-fab"
          aria-label={tr("Abrir agenda")}
          className="fixed right-4 z-40 h-12 rounded-full bg-primary text-primary-foreground font-display font-bold text-sm flex items-center justify-center gap-2 shadow-xl"
          style={{ width: AGENDA_FAB_WIDTH_PX, boxShadow: '0 10px 28px rgba(245, 197, 24, 0.38)', bottom: `calc(6rem + ${bannerVisible ? PWA_BANNER_LIFT_PX : 0}px)` }}
        >
          <CalendarDays className="w-5 h-5" strokeWidth={2.5} />{tr("Agenda")}</motion.button>
      )}
      <AgendaDock open={open} initialTab={initialTab} onClose={() => setOpen(false)} />
    </>
  );
};

export default AgendaFab;
